// components/sessions/MessageTimeline.js
import React, { useState } from 'react';
import {
  Box, Typography, Avatar, Paper, Chip, IconButton,
  Collapse, Button, Stack, Divider
} from '@mui/material';
import {
  Person as PersonIcon,
  SmartToy as SmartToyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';

const MessageTimeline = ({ messages, themeColors }) => {
  const [expandedMessages, setExpandedMessages] = useState({});
  
  const toggleExpand = (messageId) => {
    setExpandedMessages(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };
  
  const truncateText = (text, maxLength = 200) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };
  
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('it-IT', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };
  
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('it-IT', { 
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  
  // Raggruppa messaggi per data
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.timestamp);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});
  
  if (messages.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">
          Nessun messaggio in questa sessione
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box>
      {Object.entries(groupedMessages).map(([date, dateMessages]) => (
        <Box key={date}>
          <Box sx={{ textAlign: 'center', my: 2 }}>
            <Chip 
              label={date} 
              size="small" 
              sx={{ bgcolor: 'grey.100' }}
            />
          </Box>
          
          {dateMessages.map((message) => {
            const isUser = message.role === 'user';
            const isExpanded = expandedMessages[message.id];
            const needsExpansion = message.content?.length > 200;
            
            return (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  justifyContent: isUser ? 'flex-start' : 'flex-end',
                  mb: 2
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    maxWidth: '70%',
                    flexDirection: isUser ? 'row' : 'row-reverse'
                  }}
                >
                  <Avatar
                    sx={{ 
                      bgcolor: isUser ? 'grey.400' : themeColors.primary,
                      width: 32,
                      height: 32
                    }}
                    src={!isUser ? message.agent_avatar : undefined}
                  >
                    {isUser ? <PersonIcon /> : <SmartToyIcon />}
                  </Avatar>
                  
                  <Paper
                    sx={{
                      p: 1.5,
                      bgcolor: isUser ? 'grey.100' : `${themeColors.primary}10`,
                      borderRadius: 2,
                      borderTopLeftRadius: isUser ? 0 : 16,
                      borderTopRightRadius: isUser ? 16 : 0
                    }}
                  >
                    {!isUser && message.agent_name && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: themeColors.primary,
                          fontWeight: 600,
                          display: 'block',
                          mb: 0.5
                        }}
                      >
                        {message.agent_name}
                      </Typography>
                    )}
                    
                    <Typography variant="body2">
                      {isExpanded 
                        ? message.content 
                        : truncateText(message.content)
                      }
                    </Typography>
                    
                    {needsExpansion && (
                      <Button
                        size="small"
                        onClick={() => toggleExpand(message.id)}
                        endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ mt: 1 }}
                      >
                        {isExpanded ? 'Mostra meno' : 'Mostra tutto'}
                      </Button>
                    )}
                    
                    {message.attachment_count > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Chip
                          icon={<AttachFileIcon />}
                          label={`${message.attachment_count} allegati`}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    )}
                    
                    {message.mentions?.length > 0 && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {message.mentions.map((mention, idx) => (
                          <Chip
                            key={idx}
                            label={mention}
                            size="small"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        ))}
                      </Box>
                    )}
                    
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.5 }}
                    >
                      {formatTime(message.timestamp)}
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

export default MessageTimeline;